// BookTickets/stages/DateAndTime/DateAndTime.tsx
'use client'

import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import cn from 'classnames'
import { startOfDay, isPast } from 'date-fns'
import { useTranslations } from 'next-intl'

import styles from './index.module.scss'
import shared from '@booking/index.module.scss'

import type { Session } from '@booking/types/booking'

import BookingList from './components/BookingList'
import BookingCalendar from './components/BookingCalendar'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import ContinueButton from '@booking/components/ContinueButton'

import ChevronLeft from './assets/chevron-left.svg'
import ChevronRight from './assets/chevron-right.svg'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import { useBookingExternal } from '@booking/state/external.context'
import { resolveCalendarLocale } from './lib/dateAndTime'
import { usePersistedSelectedDate } from './hooks/usePersistedSelectedDate'
import { useTouchGuard } from './hooks/useTouchGuard'
import { useSessionsForDate } from './hooks/useSessionsForDate'

export default function DateAndTime() {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()

  const state = useBookingState()
  const hasExtras = Boolean(state.config?.extras)
  const { dispatch, onCompleteDateTime } = useBookingActionsCtx()

  const leftColumnRef = useRef<HTMLDivElement | null>(null)
  const { shouldIgnoreTap } = useTouchGuard()

  const { selectedDate, setSelectedDate, initialised } = usePersistedSelectedDate()

  const { loading, sessions } = useSessionsForDate({
    date: selectedDate,
    enabled: initialised,
    sessionTags: state.config.sessionTags,
  })

  const [showSessions, setShowSessions] = useState(true)

  const calendarLocale = useMemo(() => resolveCalendarLocale(locale), [locale])

  const scrollListIntoViewOnMobile = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.innerWidth >= 768) return
    if (!leftColumnRef.current) return

    const bounding = leftColumnRef.current.getBoundingClientRect()
    window.scrollTo({
      top: bounding.top + document.documentElement.scrollTop + bounding.height - window.innerHeight,
      behavior: 'smooth',
    })
  }, [])

  const handleDateChange = useCallback(
    (next: Date) => {
      if (shouldIgnoreTap()) return

      // clear selection when date changes
      dispatch({ type: 'SET_SESSION', session: null })
      setSelectedDate(next)
      scrollListIntoViewOnMobile()
    },
    [dispatch, scrollListIntoViewOnMobile, setSelectedDate, shouldIgnoreTap],
  )

  const onMonthChange = useCallback(
    (activeStartDate: Date | null) => {
      if (!activeStartDate) return
      if (isPast(startOfDay(activeStartDate))) {
        setSelectedDate(new Date())
        return
      }
      setSelectedDate(activeStartDate)
    },
    [setSelectedDate],
  )

  const bookSession = useCallback(
    (s: Session) => {
      dispatch({ type: 'SET_SESSION', session: s })
    },
    [dispatch],
  )

  const onContinue = useCallback(() => {
    if (!state.session) return
    return onCompleteDateTime()
  }, [onCompleteDateTime, state.session])

  return (
    <div className={cn(shared.box, styles.wrapper)}>
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)} ref={leftColumnRef}>
          <BackButton />
          <h2 className={shared.title}>
            {t(hasExtras ? 'ticket.selectDate.titleAlt' : 'ticket.selectDate.title')}
          </h2>

          <div className={cn(styles.left)}>
            <BookingCalendar
              value={selectedDate}
              locale={calendarLocale}
              minDate={new Date()}
              onChange={handleDateChange}
              onViewChange={(view) => setShowSessions(view === 'month')}
              onMonthChange={onMonthChange}
              prevLabel={<ChevronLeft />}
              nextLabel={<ChevronRight />}
              specialLabel={t('special')}
              renderSpecialPopover={(label) => (
                <div className={cn('popover', styles.popover)}>*{label}</div>
              )}
            />

            {showSessions ? (
              <div className={cn(styles.bookingList)}>
                <BookingList
                  key={selectedDate.toISOString()}
                  date={selectedDate}
                  loading={loading}
                  sessions={sessions}
                  onBook={bookSession}
                />
              </div>
            ) : (
              <div className={cn(styles.bottomBorder)} />
            )}
          </div>

          <ContinueButton className={cn(shared.continue)} onContinue={onContinue} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={onContinue} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}
